import React, { useRef } from 'react';
import styled from 'styled-components';
import flagSprite from '@lt/components/components/Flags';

import { useIntersectionObserver } from 'usehooks-ts';

// Components:
import CountryList from './components/CountryList';
import ResortList from './components/ResortList';

// Types:
import type { ModifierStyles } from '../../domain/state/modifier/types';
import type { IStateProps, ILinksSection } from './types';

export const LinksSection = (props: ILinksSection & IStateProps) => {
  const {
    mapExpanded,
    modifierStyles,
    variant = 'default',
    isMetaregion = false,
    isMobile,
  } = props;

  const ref = useRef<HTMLDivElement | null>(null);
  const entry = useIntersectionObserver(ref, { freezeOnceVisible: true });
  const isVisible = !!entry?.isIntersecting || isMetaregion;

  const isCountryList = ['default', 'countryList'].includes(variant);
  const isResortList = ['default', 'resortList'].includes(variant);

  return (
    <StyledContainer
      $mapExpanded={mapExpanded}
      $isMobile={isMobile}
      $modifierStyles={modifierStyles}
      $isMetaregion={isMetaregion}
      $isFlagSprite={isCountryList && isVisible}
      ref={ref}
    >
      {isCountryList && (
        <CountryList
          isMobile={isMobile}
          mapExpanded={mapExpanded}
          isMetaregion={isMetaregion}
        />
      )}
      {isResortList && <ResortList mapExpanded={mapExpanded} />}
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{
  $isMobile: boolean;
  $mapExpanded: boolean;
  $modifierStyles: ModifierStyles;
  $isMetaregion: boolean;
  $isFlagSprite: boolean;
}>`
  min-width: ${({ $isMobile, $mapExpanded }) =>
    $isMobile || $mapExpanded ? 320 : 700}px;
  margin-bottom: ${({ $isMobile, $isMetaregion }) =>
    $isMobile || $isMetaregion ? 8 : 16}px;
  margin-top: ${({ $isMobile }) => ($isMobile ? 8 : 16)}px;

  ${({ $isMetaregion }) =>
    $isMetaregion &&
    `
    margin-top: 0;
  `}

  ${({ $isFlagSprite }) => ($isFlagSprite ? flagSprite() : '')}

  ${({ $isMobile }) =>
    $isMobile &&
    `
    & a, 
    & button {
      display: inline-block;

      min-width: 48px;
      padding: 4px 0;

      font-size: 16px;
      line-height: 20px;
    }
  `}

  ${({ $isMobile, $modifierStyles }) =>
    $isMobile &&
    $modifierStyles.textColor &&
    `
    h2, p, button {
      color: ${$modifierStyles.textColor} !important;
    }

    a {
      color: ${$modifierStyles.linkColor} !important;
    }

    svg {
      path {
        fill: ${$modifierStyles.textColor} !important;
      }
    }
  `}
`;
